import React, { FC } from 'react';
import PollEntity from '../../../../store/polls-store/poll-entity';
import HideForPresenter from '../../shared/hide-for-presenter';
import {
  PollsTabsOnMobile,
  PollsViewStatuses,
  FullScreenButtonContent
} from '../../../../constans/polls-constants';
import OpenCloseButton from '../../shared/open-close-button';
import PollStatus from '../../shared/poll-status/poll-status';
import { observer } from 'mobx-react';
import ElipsisMenuWrapper from '../../shared/elipsis-menu-wrapper';
import FullScreenButton from '../../shared/full-screen-button';
import ResultSlideButton from '../../shared/result-slide-button';
import ShowResultsButton from '../../shared/show-results-button';
import { getMixpanelService } from '../../../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../../../constans/mixpanel-contants';
import { isSafari } from '../../../../constans/url-constants';

interface IProps {
  poll: PollEntity;
  changePollStatus: (poll: PollEntity, customStatus?: PollsViewStatuses) => Promise<void>;
  isEllipsisMenuOpened: boolean;
  setShouldShowEllipsisMenu: (value: boolean) => void;
  setShouldShowDeleteModal: (value: boolean) => void;
  setShouldShowResetModal: (value: boolean) => void;
  setShouldShowFullScreen: (value: boolean) => void;
  setShouldShowAddResultsSlideModal: (value: boolean) => void;
  toggleManagePollModal: () => void;
  setActiveMobileTab: (tab: PollsTabsOnMobile) => void;
  isMobile: boolean;
  isOnePollOpened: boolean;
  isSomePollResultsShared: boolean;
  isNextGenPlayer: boolean;
  sharePollResults: (poll: PollEntity, resultsShared: boolean) => Promise<void>;
  setIsFullScreenModeSafari: (value: boolean) => void;
}

const PollDetailsHeader: FC<IProps> = ({
  poll,
  changePollStatus,
  isEllipsisMenuOpened,
  setShouldShowEllipsisMenu,
  setShouldShowDeleteModal,
  setShouldShowResetModal,
  setShouldShowAddResultsSlideModal,
  toggleManagePollModal,
  setShouldShowFullScreen,
  setActiveMobileTab,
  isMobile,
  isOnePollOpened,
  isSomePollResultsShared,
  isNextGenPlayer,
  sharePollResults,
  setIsFullScreenModeSafari
}) => {
  const status = poll.status || PollsViewStatuses.NOT_STARTED;
  const shouldShowOpenCloseBtn = status !== PollsViewStatuses.COMPLETED && status !== PollsViewStatuses.RESETED;
  // Locked out while another poll is currently sharing its results (Next-Gen live/published only).
  const isResultsSharingLockedByOtherPoll = isNextGenPlayer && isSomePollResultsShared && !poll.resultsShared;
  // Desktop already disabled Open Poll based on isOnePollOpened before this feature — preserve that
  // (identical for Legacy) and only add the new results-sharing lock on top.
  const isOpenButtonDisabled = status === PollsViewStatuses.NOT_STARTED && (isOnePollOpened || isResultsSharingLockedByOtherPoll);
  // The mobile Open Poll button was never disabled before this feature — keep that untouched for
  // Legacy and only add the new results-sharing lock, which is always false for Legacy.
  const isMobileOpenButtonDisabled = status === PollsViewStatuses.NOT_STARTED && isResultsSharingLockedByOtherPoll;
  // This poll's own ellipsis menu locks down entirely while it's the one sharing results.
  const isActivePollSharingResults = isNextGenPlayer && poll.resultsShared;
  // Legacy's Add Results Slide button — unchanged rule.
  const shouldShowResultSlideBtn = status === PollsViewStatuses.COMPLETED;
  // Next-Gen's Show/Hide Results toggle needs at least one attendee response: there is nothing to share
  // from a closed poll nobody answered.
  const shouldShowShareResultsBtn = shouldShowResultSlideBtn && poll.countVotes > 0;

  const changePollStatusHandler = (): void => {
    changePollStatus(poll);
  };

  const openInNewFullScreenWindow = (): void => {
    if (isSafari) {
      setIsFullScreenModeSafari(true);
    }

    setShouldShowFullScreen(true);
    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.POLLS, MIXPANEL_EVENT_TYPES.FULLSCREEN);
  };

  return (
    <div className='poll-details-header'>
      <div className='poll-details-header-info'>
        <div className='poll-details-header-presenter'>
          <HideForPresenter hideForPresenter={poll.hideForPresenter}/>
          {isMobile &&
            <ElipsisMenuWrapper
              poll={poll}
              isEllipsisMenuOpened={isEllipsisMenuOpened}
              setShouldShowEllipsisMenu={setShouldShowEllipsisMenu}
              setShouldShowDeleteModal={setShouldShowDeleteModal}
              setShouldShowResetModal={setShouldShowResetModal}
              toggleManagePollModal={toggleManagePollModal}
              setActiveMobileTab={setActiveMobileTab}
              changePollStatus={changePollStatus}
              isMobile={isMobile}
              isOnePollOpened={isOnePollOpened}
              isResultsSharingLockedByOtherPoll={isResultsSharingLockedByOtherPoll}
              isActivePollSharingResults={isActivePollSharingResults}
            />
          }
        </div>
        <div className='poll-details-header-question'>{poll.value}</div>
        <div className='status-votes-container'>
          <div className='status-votes-container-info'>
            <PollStatus status={status} additionalClassName='poll-details-header-status'/>
            {status !== PollsViewStatuses.NOT_STARTED && <span className='votes'>{poll.getVisibleVotesCount()} Votes</span>}
          </div>
          {shouldShowOpenCloseBtn && isMobile && <OpenCloseButton disabled={isMobileOpenButtonDisabled} onClick={changePollStatusHandler} status={status}/>}
        </div>
      </div>
      <div className='poll-details-header-actions'>
        {shouldShowOpenCloseBtn && !isMobile && <OpenCloseButton disabled={isOpenButtonDisabled} onClick={changePollStatusHandler} status={status}/>}
        {isNextGenPlayer
          ? shouldShowShareResultsBtn && (
            <ShowResultsButton
              resultsShared={poll.resultsShared}
              disabled={isOnePollOpened || isResultsSharingLockedByOtherPoll}
              onClick={() => sharePollResults(poll, !poll.resultsShared)}
            />
          )
          : shouldShowResultSlideBtn && (
            <ResultSlideButton
              openConfirmationModal={setShouldShowAddResultsSlideModal}
              showButton={!poll.result_slide_added}
              isLoading={poll.shouldShowGenerating}
            />
          )
        }
        <FullScreenButton buttonStateContent={FullScreenButtonContent.OPEN} actionHandler={openInNewFullScreenWindow}/>
      </div>
    </div>
  );
};

export default observer(PollDetailsHeader);
